/**
 * The menu import (ADR-044): read a Loyverse items export and the owner's modifier sheet, compare
 * them with the catalog as it stands, and plan the catalog commands that make the catalog match.
 *
 * Nothing here writes. The plan is a list of ordinary catalog commands (the same ones the Catalog
 * screens send), so that:
 * - a dry run shows exactly what an import would do, and every problem, before anything is written;
 * - running the plan again after a part of it was applied sends only what is still missing (new
 *   rows have fixed ids, and the plan is the difference between the file and the catalog);
 * - a command retried inside one run after a dropped connection is recognised by the server
 *   (command replay, importRunCommandId), while the next run sends fresh command ids.
 * Only catalog commands are ever planned: an import creates no order, payment, cost history or
 * any other money record. Rows that exist in the catalog but not in the file are left alone.
 */
import {
  isValidBarcode,
  isValidSku,
  normalizeBarcode,
  normalizeSku,
  parseMoneyToMinor,
  validateModifierRule,
} from './catalog';
import type { CommandPayloads, ExternalRefs } from './commands';
import { uuidv5 } from './ids';

/** The namespace of every id the import derives (UUIDv5). Never change it: ids would no longer match. */
export const MENU_IMPORT_NAMESPACE = 'c77c3e66-9b5b-4de6-9e53-343990dc6318';

/** The catalog's own limits (supabase/migrations/20260926001600_catalog_schema.sql). */
export const CATALOG_LIMITS = {
  categoryName: 60,
  productName: 120,
  description: 2000,
  variantName: 60,
  groupName: 60,
  optionName: 60,
  /** min_select and max_select of a modifier group (the commands take 0 to 100). */
  modifierSelect: 100,
} as const;

/** The only commands an import may send: catalog commands, nothing about money or people. */
export const IMPORT_COMMAND_KINDS = [
  'modifier_group.create',
  'modifier_group.update',
  'modifier_group.restore',
  'modifier_option.create',
  'modifier_option.update',
  'modifier_option.restore',
  'category.create',
  'category.update',
  'category.restore',
  'product.create',
  'product.update',
  'product.restore',
  'variant.update',
  'variant.restore',
  'variant.create',
  'product.assign_modifier_group',
] as const;
export type ImportCommandKind = (typeof IMPORT_COMMAND_KINDS)[number];

export function isImportCommandKind(kind: string): kind is ImportCommandKind {
  return (IMPORT_COMMAND_KINDS as readonly string[]).includes(kind);
}

// ───────────────────────── CSV ─────────────────────────

/**
 * RFC 4180 CSV: quoted fields with commas, doubled quotes and line breaks; LF or CRLF; a leading
 * byte-order mark is dropped. Blank lines are skipped. Cells are returned as written (not trimmed).
 */
export function parseCsv(text: string): string[][] {
  const src = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  let i = 0;
  const endRow = () => {
    row.push(cell);
    cell = '';
    if (!(row.length === 1 && row[0] === '')) rows.push(row);
    row = [];
  };
  while (i < src.length) {
    const ch = src[i]!;
    if (quoted) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          cell += '"';
          i += 2;
          continue;
        }
        quoted = false;
        i++;
        continue;
      }
      cell += ch;
      i++;
      continue;
    }
    if (ch === '"' && cell === '') {
      quoted = true;
      i++;
    } else if (ch === ',') {
      row.push(cell);
      cell = '';
      i++;
    } else if (ch === '\r' && src[i + 1] === '\n') {
      endRow();
      i += 2;
    } else if (ch === '\n' || ch === '\r') {
      endRow();
      i++;
    } else {
      cell += ch;
      i++;
    }
  }
  if (cell !== '' || row.length > 0) endRow();
  return rows;
}

// ───────────────────────── problems ─────────────────────────

export type ProblemLevel = 'error' | 'warning' | 'info';
/** An error blocks the import; a warning says what will be skipped or changed; info is context. */
export interface ImportProblem {
  level: ProblemLevel;
  where: string;
  message: string;
}

// ───────────────────────── the Loyverse items export ─────────────────────────

export type PriceCell =
  { kind: 'fixed'; minor: number } | { kind: 'variable' } | { kind: 'invalid'; text: string };

export interface LoyverseVariant {
  /** The 1-based line of the file (the header is line 1). */
  line: number;
  sku: string;
  optionValues: string[];
  price: PriceCell;
  /** Minor units; null when empty; 'invalid' when not an amount. */
  cost: number | null | 'invalid';
  barcode: string | null;
  forSale: boolean;
}

export interface LoyverseItem {
  /** The Loyverse handle (or `sku:<SKU>` for an item without one). */
  key: string;
  line: number;
  name: string;
  category: string;
  description: string;
  soldByWeight: boolean;
  trackStock: boolean;
  composite: boolean;
  modifiers: string[];
  variants: LoyverseVariant[];
}

export interface LoyverseFile {
  items: LoyverseItem[];
  /** Store names found in the per-store columns (`Price [<store>]`). */
  stores: string[];
  /** The store whose price and availability were read; null when it could not be chosen. */
  store: string | null;
  modifierColumns: string[];
  taxColumns: string[];
  problems: ImportProblem[];
}

const REQUIRED_COLUMNS = ['Handle', 'SKU', 'Name', 'Category'] as const;

function yes(cell: string | undefined): boolean {
  return (cell ?? '').trim().toUpperCase() === 'Y';
}

/**
 * Read a Loyverse items export (Back Office, Items, Export). One row per variant, rows of an item
 * share its Handle; item fields are on its first row; composite components are extra rows with an
 * empty Handle and SKU. `store` picks the price and availability columns when there are several.
 */
export function readLoyverseItems(
  rows: string[][],
  options: { store?: string | null; fractionDigits?: number } = {},
): LoyverseFile {
  const fd = options.fractionDigits ?? 2;
  const problems: ImportProblem[] = [];
  const file: LoyverseFile = {
    items: [],
    stores: [],
    store: null,
    modifierColumns: [],
    taxColumns: [],
    problems,
  };
  const header = (rows[0] ?? []).map((h) => h.trim());
  if (header.length === 0) {
    problems.push({ level: 'error', where: 'File', message: 'The file is empty.' });
    return file;
  }
  const col = new Map<string, number>();
  header.forEach((h, i) => {
    if (!col.has(h)) col.set(h, i);
  });
  const missing = REQUIRED_COLUMNS.filter((c) => !col.has(c));
  if (missing.length > 0) {
    problems.push({
      level: 'error',
      where: 'File',
      message: `This is not a Loyverse items export: the column${missing.length > 1 ? 's' : ''} ${missing.join(', ')} ${missing.length > 1 ? 'are' : 'is'} missing.`,
    });
    return file;
  }
  for (const h of header) {
    const store = /^Price \[(.+)\]$/.exec(h)?.[1];
    if (store !== undefined && !file.stores.includes(store)) file.stores.push(store);
    const mod = /^Modifier - "(.*)"$/.exec(h)?.[1];
    if (mod !== undefined) file.modifierColumns.push(mod);
    if (/^Tax - "/.test(h)) file.taxColumns.push(h.replace(/^Tax - /, ''));
  }
  const hasDefaultPrice = col.has('Default price');
  if (file.stores.length === 0 && !hasDefaultPrice) {
    problems.push({
      level: 'error',
      where: 'File',
      message: 'No price column was found (Price [store] or Default price).',
    });
    return file;
  }
  if (file.stores.length === 1) file.store = file.stores[0]!;
  else if (file.stores.length > 1) {
    if (options.store && file.stores.includes(options.store)) file.store = options.store;
    else {
      problems.push({
        level: 'error',
        where: 'File',
        message: `The file has ${file.stores.length} stores (${file.stores.join(', ')}): choose the one whose prices to import.`,
      });
      return file;
    }
  }
  const get = (r: string[], name: string) => {
    const i = col.get(name);
    return i === undefined ? '' : (r[i] ?? '');
  };
  const modifierIndex = file.modifierColumns.map((m) => col.get(`Modifier - "${m}"`)!);
  const byKey = new Map<string, LoyverseItem>();
  let current: LoyverseItem | null = null;

  rows.slice(1).forEach((r, idx) => {
    const line = idx + 2;
    if (r.every((c) => c.trim() === '')) return;
    const handle = get(r, 'Handle').trim();
    const sku = get(r, 'SKU').trim();
    if (!handle && !sku) {
      if (get(r, 'SKU of included item').trim() && current) {
        current.composite = true;
        return;
      }
      problems.push({ level: 'error', where: `Line ${line}`, message: 'No Handle and no SKU.' });
      return;
    }
    const key = handle || `sku:${sku}`;
    let item = byKey.get(key);
    const name = get(r, 'Name').trim();
    const category = get(r, 'Category').trim();
    if (!item) {
      item = {
        key,
        line,
        name,
        category,
        description: get(r, 'Description'),
        soldByWeight: yes(get(r, 'Sold by weight')),
        trackStock: yes(get(r, 'Track stock')),
        composite: get(r, 'SKU of included item').trim() !== '',
        modifiers: [],
        variants: [],
      };
      byKey.set(key, item);
      file.items.push(item);
    } else {
      // Loyverse refuses a later row whose item fields differ; so does the import
      if (name && name !== item.name)
        problems.push({
          level: 'error',
          where: `Line ${line}`,
          message: `"${name}" differs from the item's name on line ${item.line} ("${item.name}") for handle ${key}.`,
        });
      if (category && category !== item.category)
        problems.push({
          level: 'error',
          where: `Line ${line}`,
          message: `Category "${category}" differs from line ${item.line} ("${item.category}") for handle ${key}.`,
        });
      if (get(r, 'SKU of included item').trim()) item.composite = true;
    }
    current = item;
    file.modifierColumns.forEach((m, i) => {
      if (yes(r[modifierIndex[i]!]) && !item.modifiers.includes(m)) item.modifiers.push(m);
    });

    const storePrice = file.store ? get(r, `Price [${file.store}]`).trim() : '';
    const defaultPrice = hasDefaultPrice ? get(r, 'Default price').trim() : '';
    const priceText = storePrice !== '' ? storePrice : defaultPrice;
    let price: PriceCell;
    if (priceText === '' || priceText.toLowerCase() === 'variable') price = { kind: 'variable' };
    else {
      const minor = parseMoneyToMinor(priceText, { fractionDigits: fd });
      price = minor === null ? { kind: 'invalid', text: priceText } : { kind: 'fixed', minor };
    }
    const costText = get(r, 'Cost').trim();
    const cost =
      costText === '' ? null : (parseMoneyToMinor(costText, { fractionDigits: fd }) ?? 'invalid');
    const availability = file.store ? get(r, `Available for sale [${file.store}]`).trim() : '';
    item.variants.push({
      line,
      sku,
      optionValues: [
        get(r, 'Option 1 value'),
        get(r, 'Option 2 value'),
        get(r, 'Option 3 value'),
      ].map((v) => v.trim()),
      price,
      cost,
      barcode: normalizeBarcode(get(r, 'Barcode')),
      forSale: availability.toUpperCase() !== 'N',
    });
  });
  return file;
}

/** Categories of the file with how many items each holds ('' is "no category"), in file order. */
export function categoriesOf(file: LoyverseFile): { key: string; name: string; items: number }[] {
  const out: { key: string; name: string; items: number }[] = [];
  for (const item of file.items) {
    const key = item.category.toLowerCase();
    const found = out.find((c) => c.key === key);
    if (found) found.items++;
    else out.push({ key, name: item.category, items: 1 });
  }
  return out;
}

// ───────────────────────── the owner's modifier sheet ─────────────────────────

export interface SheetGroup {
  line: number;
  name: string;
  min: number;
  max: number | null;
  options: { line: number; name: string; priceMinor: number }[];
}
export interface ModifierSheet {
  groups: SheetGroup[];
  problems: ImportProblem[];
}

/** The header of the modifier sheet the owner fills (Loyverse does not export modifier options). */
export const MODIFIER_SHEET_HEADER = ['Modifier', 'Option', 'Price', 'Min', 'Max'] as const;

/**
 * Read the modifier sheet: one row per option, `Modifier,Option,Price,Min,Max`. Price is the
 * amount the option adds (empty is 0; may be negative). Min and Max are the group's selection rule
 * (empty Min is 0, empty Max is no limit); they may be given on any row of the group, once.
 */
export function readModifierSheet(
  rows: string[][],
  options: { fractionDigits?: number } = {},
): ModifierSheet {
  const fd = options.fractionDigits ?? 2;
  const problems: ImportProblem[] = [];
  const sheet: ModifierSheet = { groups: [], problems };
  const header = (rows[0] ?? []).map((h) => h.trim().toLowerCase());
  const idx = (name: string) => header.indexOf(name.toLowerCase());
  const missing = MODIFIER_SHEET_HEADER.filter(
    (h) => idx(h) < 0 && (h === 'Modifier' || h === 'Option'),
  );
  if (missing.length > 0) {
    problems.push({
      level: 'error',
      where: 'Modifier sheet',
      message: `The first row must be ${MODIFIER_SHEET_HEADER.join(',')}.`,
    });
    return sheet;
  }
  const cell = (r: string[], name: string) => {
    const i = idx(name);
    return i < 0 ? '' : (r[i] ?? '').trim();
  };
  const ruleSeen = new Map<string, { min?: number; max?: number | null; line: number }>();
  rows.slice(1).forEach((r, n) => {
    const line = n + 2;
    if (r.every((c) => c.trim() === '')) return;
    const where = `Modifier sheet line ${line}`;
    const groupName = cell(r, 'Modifier');
    const optionName = cell(r, 'Option');
    if (!groupName) {
      problems.push({ level: 'error', where, message: 'The Modifier column is empty.' });
      return;
    }
    let group = sheet.groups.find((g) => g.name.toLowerCase() === groupName.toLowerCase());
    if (!group) {
      group = { line, name: groupName, min: 0, max: null, options: [] };
      sheet.groups.push(group);
    }
    const key = groupName.toLowerCase();
    const seen = ruleSeen.get(key) ?? { line };
    const minText = cell(r, 'Min');
    const maxText = cell(r, 'Max');
    if (minText !== '') {
      const v = Number(minText);
      if (!/^\d+$/.test(minText))
        problems.push({
          level: 'error',
          where,
          message: `Min "${minText}" is not a whole number.`,
        });
      else if (seen.min !== undefined && seen.min !== v)
        problems.push({
          level: 'error',
          where,
          message: `Min differs from line ${seen.line} for "${groupName}".`,
        });
      else {
        seen.min = v;
        group.min = v;
      }
    }
    if (maxText !== '') {
      const v = Number(maxText);
      if (!/^\d+$/.test(maxText))
        problems.push({
          level: 'error',
          where,
          message: `Max "${maxText}" is not a whole number.`,
        });
      else if (seen.max !== undefined && seen.max !== v)
        problems.push({
          level: 'error',
          where,
          message: `Max differs from line ${seen.line} for "${groupName}".`,
        });
      else {
        seen.max = v;
        group.max = v;
      }
    }
    ruleSeen.set(key, seen);
    if (!optionName) {
      problems.push({
        level: 'error',
        where,
        message: `"${groupName}" has a row without an Option.`,
      });
      return;
    }
    if (group.options.some((o) => o.name.toLowerCase() === optionName.toLowerCase())) {
      problems.push({
        level: 'error',
        where,
        message: `"${optionName}" is listed twice in "${groupName}".`,
      });
      return;
    }
    const priceText = cell(r, 'Price');
    const priceMinor =
      priceText === ''
        ? 0
        : parseMoneyToMinor(priceText, { fractionDigits: fd, allowNegative: true });
    if (priceMinor === null) {
      problems.push({ level: 'error', where, message: `Price "${priceText}" is not an amount.` });
      return;
    }
    group.options.push({ line, name: optionName, priceMinor });
  });
  return sheet;
}

// ───────────────────────── the catalog as it stands ─────────────────────────

export interface SnapshotCategory {
  id: string;
  name: string;
  archived_at: string | null;
  external_refs: unknown;
}
export interface SnapshotProduct {
  id: string;
  name: string;
  category_id: string | null;
  description: string | null;
  production_route_id: string | null;
  archived_at: string | null;
  external_refs: unknown;
}
export interface SnapshotVariant {
  id: string;
  product_id: string;
  name: string | null;
  sku: string | null;
  barcode: string | null;
  price_minor: number;
  is_default: boolean;
  archived_at: string | null;
}
export interface SnapshotGroup {
  id: string;
  name: string;
  min_select: number;
  max_select: number | null;
  archived_at: string | null;
  external_refs: unknown;
}
export interface SnapshotOption {
  id: string;
  modifier_group_id: string;
  name: string;
  price_delta_minor: number;
  archived_at: string | null;
}
/** The business's catalog, archived rows included, as the owner reads it. */
export interface CatalogSnapshot {
  categories: SnapshotCategory[];
  products: SnapshotProduct[];
  variants: SnapshotVariant[];
  costs: { product_variant_id: string; cost_minor: number }[];
  groups: SnapshotGroup[];
  options: SnapshotOption[];
  assignments: { product_id: string; modifier_group_id: string }[];
  routes: { id: string; code: string }[];
}

export const EMPTY_SNAPSHOT: CatalogSnapshot = {
  categories: [],
  products: [],
  variants: [],
  costs: [],
  groups: [],
  options: [],
  assignments: [],
  routes: [],
};

// ───────────────────────── the plan ─────────────────────────

/** Where the lines of a category's products are made: the Kitchen, the Bar, or nowhere. */
export type RouteChoice = 'KITCHEN' | 'BAR' | 'NONE';

export interface ImportSettings {
  businessId: string;
  /** By category key (the lower-cased name; '' for items without a category). */
  routeByCategory: Record<string, RouteChoice | undefined>;
  /** Import each variant's Loyverse cost as today's cost (one current figure, no history). */
  importCosts: boolean;
  /** Leave out items sold by weight or with a variable price instead of refusing the file. */
  skipUnsupported: boolean;
}

type Payload<K extends ImportCommandKind> = CommandPayloads[K];
export type PlannedStep = {
  [K in ImportCommandKind]: {
    kind: K;
    payload: Payload<K>;
    /**
     * The step's fixed id: the same file, settings and catalog give the same id (the plan shown is
     * the plan sent). It is not the command id: each import run sends `importRunCommandId(run, step)`.
     */
    stepId: string;
    entity:
      'modifier group' | 'modifier option' | 'category' | 'product' | 'variant' | 'assignment';
    action: 'create' | 'update' | 'restore' | 'assign';
    /** What a person reads in the plan, e.g. 'Iced Coffee: price ₱120.00 → ₱130.00'. */
    label: string;
  };
}[ImportCommandKind];

export interface ImportCounts {
  create: number;
  update: number;
  restore: number;
  assign: number;
  unchangedItems: number;
  importedItems: number;
  skippedItems: number;
}

export interface ImportPlan {
  steps: PlannedStep[];
  problems: ImportProblem[];
  counts: ImportCounts;
  /** True when an error stops the import. */
  blocked: boolean;
}

/** JSON with sorted keys, so that the same payload always gives the same command id. */
export function stableJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableJson).join(',')}]`;
  if (value && typeof value === 'object')
    return `{${Object.keys(value as Record<string, unknown>)
      .sort()
      .filter((k) => (value as Record<string, unknown>)[k] !== undefined)
      .map((k) => `${JSON.stringify(k)}:${stableJson((value as Record<string, unknown>)[k])}`)
      .join(',')}}`;
  return JSON.stringify(value);
}

/** The id an imported row gets: the same business, kind and Loyverse key give the same id. */
export function importEntityId(businessId: string, entity: string, key: string): string {
  return uuidv5(`${businessId}|${entity}|${key}`, MENU_IMPORT_NAMESPACE);
}

function importStepId(businessId: string, kind: string, key: string, payload: unknown): string {
  return uuidv5(
    `${businessId}|command|${kind}|${key}|${stableJson(payload)}`,
    MENU_IMPORT_NAMESPACE,
  );
}

/**
 * The command id a step is sent with in one import run. A retry inside the run (a dropped
 * connection) reuses it, so the server never applies a step twice; the next run gets new ids, so a
 * change the owner undid by hand in Catalog is applied again rather than answered from the server's
 * memory of the earlier run. Rows are never created twice either way: their ids are fixed
 * (importEntityId) and the next run's plan only holds what the catalog still lacks.
 */
export function importRunCommandId(runId: string, step: Pick<PlannedStep, 'stepId'>): string {
  return uuidv5(`run|${runId}|${step.stepId}`, MENU_IMPORT_NAMESPACE);
}

function loyverseRef(row: { external_refs: unknown }): Record<string, unknown> | null {
  const refs = row.external_refs;
  if (!refs || typeof refs !== 'object') return null;
  const l = (refs as Record<string, unknown>).loyverse;
  return l && typeof l === 'object' ? (l as Record<string, unknown>) : null;
}

/** Loyverse descriptions are HTML: keep the text, one line per paragraph or list item. */
export function htmlToText(html: string): string {
  const text = html
    .replace(/<\s*(br|\/p|\/li|\/div|\/h\d)\s*\/?>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;|&apos;/g, "'")
    .replace(/&amp;/g, '&');
  return text
    .split('\n')
    .map((l) => l.replace(/\s+/g, ' ').trim())
    .filter((l) => l !== '')
    .join('\n');
}

function money(minor: number): string {
  return (minor / 100).toFixed(2);
}

const lower = (s: string) => s.toLowerCase();

/**
 * Compare the file with the catalog and plan the commands that make the catalog match it.
 * Matching never relies on names alone for products: an existing product with the same name that
 * did not come from this import is reported as a conflict, never overwritten.
 */
export function planMenuImport(
  file: LoyverseFile,
  sheet: ModifierSheet | null,
  snapshot: CatalogSnapshot,
  settings: ImportSettings,
): ImportPlan {
  const problems: ImportProblem[] = [...file.problems, ...(sheet?.problems ?? [])];
  const counts: ImportCounts = {
    create: 0,
    update: 0,
    restore: 0,
    assign: 0,
    unchangedItems: 0,
    importedItems: 0,
    skippedItems: 0,
  };
  const groupSteps: PlannedStep[] = [];
  const optionSteps: PlannedStep[] = [];
  const categorySteps: PlannedStep[] = [];
  const productSteps: PlannedStep[] = [];
  const variantSteps: PlannedStep[] = [];
  const assignSteps: PlannedStep[] = [];
  const err = (where: string, message: string) => problems.push({ level: 'error', where, message });
  const warn = (where: string, message: string) =>
    problems.push({ level: 'warning', where, message });
  const info = (where: string, message: string) => problems.push({ level: 'info', where, message });
  const biz = settings.businessId;
  const step = <K extends ImportCommandKind>(
    list: PlannedStep[],
    kind: K,
    key: string,
    payload: Payload<K>,
    entity: PlannedStep['entity'],
    action: PlannedStep['action'],
    label: string,
  ) => {
    list.push({
      kind,
      payload,
      stepId: importStepId(biz, kind, key, payload),
      entity,
      action,
      label,
    } as PlannedStep);
    if (action === 'create') counts.create++;
    else if (action === 'update') counts.update++;
    else if (action === 'restore') counts.restore++;
    else counts.assign++;
  };

  if (file.items.length === 0 && !file.problems.some((p) => p.level === 'error'))
    err('File', 'The file has no items.');
  if (file.taxColumns.length > 0)
    info(
      'Taxes',
      `Tax columns are not imported (${file.taxColumns.join(', ')}): tax rules wait for the accountant (ADR-003).`,
    );

  // ── modifier groups and options (from the owner's sheet) ──
  const groupIdByName = new Map<string, string>();
  for (const g of sheet?.groups ?? []) {
    const where = `Modifier "${g.name}"`;
    if (g.name.length > CATALOG_LIMITS.groupName) {
      err(where, `The name is longer than ${CATALOG_LIMITS.groupName} characters.`);
      continue;
    }
    const rule = { min_select: g.min, max_select: g.max };
    if (g.min > CATALOG_LIMITS.modifierSelect || (g.max ?? 0) > CATALOG_LIMITS.modifierSelect) {
      err(where, `Min and Max are at most ${CATALOG_LIMITS.modifierSelect}.`);
      continue;
    }
    const ruleProblem = validateModifierRule(rule);
    if (ruleProblem) {
      err(where, ruleProblem);
      continue;
    }
    if (g.options.length === 0) {
      err(where, 'It has no options.');
      continue;
    }
    const key = lower(g.name);
    const id = importEntityId(biz, 'modifier_group', key);
    const liveGroup = snapshot.groups.find((x) => !x.archived_at && lower(x.name) === key);
    let existing =
      snapshot.groups.find((x) => x.id === id) ??
      snapshot.groups.find((x) => loyverseRef(x)?.modifier === key) ??
      liveGroup;
    // restoring an archived group next to a live one of the same name would be refused
    if (existing?.archived_at && liveGroup) existing = liveGroup;
    const groupId = existing?.id ?? id;
    groupIdByName.set(key, groupId);
    if (!existing) {
      step(
        groupSteps,
        'modifier_group.create',
        key,
        {
          id,
          name: g.name,
          min_select: g.min,
          max_select: g.max,
          external_refs: { loyverse: { modifier: key } },
        },
        'modifier group',
        'create',
        `Modifier group "${g.name}"`,
      );
    } else {
      if (existing.archived_at)
        step(
          groupSteps,
          'modifier_group.restore',
          key,
          { modifier_group_id: existing.id },
          'modifier group',
          'restore',
          `Restore modifier group "${g.name}"`,
        );
      const changes: Payload<'modifier_group.update'> = { modifier_group_id: existing.id };
      if (existing.name !== g.name) changes.name = g.name;
      if (existing.min_select !== g.min) changes.min_select = g.min;
      if (existing.max_select !== g.max) changes.max_select = g.max;
      if (Object.keys(changes).length > 1)
        step(
          groupSteps,
          'modifier_group.update',
          key,
          changes,
          'modifier group',
          'update',
          `Modifier group "${g.name}": name or rule`,
        );
    }
    for (const o of g.options) {
      const owhere = `${where}, option "${o.name}"`;
      if (o.name.length > CATALOG_LIMITS.optionName) {
        err(owhere, `The name is longer than ${CATALOG_LIMITS.optionName} characters.`);
        continue;
      }
      const okey = `${key}|${lower(o.name)}`;
      const oid = importEntityId(biz, 'modifier_option', okey);
      const inGroup = snapshot.options.filter((x) => x.modifier_group_id === groupId);
      const liveOption = inGroup.find((x) => !x.archived_at && lower(x.name) === lower(o.name));
      let ex = inGroup.find((x) => x.id === oid) ?? liveOption;
      if (ex?.archived_at && liveOption) ex = liveOption;
      if (!ex) {
        step(
          optionSteps,
          'modifier_option.create',
          okey,
          {
            id: oid,
            modifier_group_id: groupId,
            name: o.name,
            price_delta_minor: o.priceMinor,
            external_refs: { loyverse: { modifier: key, option: lower(o.name) } },
          },
          'modifier option',
          'create',
          `Option "${o.name}" (+${money(o.priceMinor)}) in "${g.name}"`,
        );
        continue;
      }
      if (ex.archived_at)
        step(
          optionSteps,
          'modifier_option.restore',
          okey,
          { modifier_option_id: ex.id },
          'modifier option',
          'restore',
          `Restore option "${o.name}"`,
        );
      const ch: Payload<'modifier_option.update'> = { modifier_option_id: ex.id };
      if (ex.name !== o.name) ch.name = o.name;
      if (ex.price_delta_minor !== o.priceMinor) ch.price_delta_minor = o.priceMinor;
      if (Object.keys(ch).length > 1)
        step(
          optionSteps,
          'modifier_option.update',
          okey,
          ch,
          'modifier option',
          'update',
          `Option "${o.name}": ${money(ex.price_delta_minor)} → ${money(o.priceMinor)}`,
        );
    }
  }

  // ── which items can be imported ──
  const routeIdByCode = new Map(snapshot.routes.map((r) => [r.code, r.id]));
  const importable: { item: LoyverseItem; variants: LoyverseVariant[] }[] = [];
  const skuOwner = new Map<string, string>();
  const barcodeOwner = new Map<string, string>();
  const nameOwner = new Map<string, string>();
  const missingModifiers = new Map<string, number>();
  let costsIgnored = 0;
  let stockIgnored = 0;
  for (const item of file.items) {
    const where = `"${item.name || item.key}" (line ${item.line})`;
    const reasons: string[] = [];
    if (item.soldByWeight) reasons.push('it is sold by weight');
    if (item.variants.some((v) => v.price.kind === 'variable'))
      reasons.push('its price is entered at the sale (variable)');
    if (reasons.length > 0) {
      const text = `didto. OS sells by unit at a fixed price, but ${reasons.join(' and ')}.`;
      if (settings.skipUnsupported) {
        warn(where, `Not imported: ${text}`);
        counts.skippedItems++;
      } else
        err(where, `${text} Give it a fixed price in Loyverse, or choose to leave such items out.`);
      continue;
    }
    const forSale = item.variants.filter((v) => v.forSale);
    if (forSale.length === 0) {
      warn(where, `Not imported: it is not available for sale in ${file.store ?? 'the store'}.`);
      counts.skippedItems++;
      continue;
    }
    if (forSale.length < item.variants.length)
      warn(
        where,
        `${item.variants.length - forSale.length} variant(s) not available for sale are left out.`,
      );
    let ok = true;
    if (!item.name) {
      err(where, 'The item has no name.');
      ok = false;
    } else if (item.name.length > CATALOG_LIMITS.productName) {
      err(where, `The name is longer than ${CATALOG_LIMITS.productName} characters.`);
      ok = false;
    } else {
      const prev = nameOwner.get(lower(item.name));
      if (prev) {
        err(
          where,
          `Another item (${prev}) has the same name; product names are unique in didto. OS.`,
        );
        ok = false;
      } else nameOwner.set(lower(item.name), where);
    }
    if (item.category.length > CATALOG_LIMITS.categoryName) {
      err(
        where,
        `The category "${item.category}" is longer than ${CATALOG_LIMITS.categoryName} characters.`,
      );
      ok = false;
    }
    const multi = forSale.length > 1;
    const variantNames = new Set<string>();
    for (const v of forSale) {
      const vwhere = `${where}, SKU ${v.sku || '(none)'} (line ${v.line})`;
      const sku = normalizeSku(v.sku);
      if (!sku) {
        err(vwhere, 'The SKU is empty; the import uses SKUs to recognise variants.');
        ok = false;
      } else if (!isValidSku(sku)) {
        err(vwhere, `The SKU "${v.sku}" can only use letters, digits and . _ / - (up to 64).`);
        ok = false;
      } else if (skuOwner.has(sku)) {
        err(vwhere, `The SKU is also used by ${skuOwner.get(sku)}.`);
        ok = false;
      } else skuOwner.set(sku, vwhere);
      if (v.barcode !== null) {
        if (!isValidBarcode(v.barcode)) {
          err(vwhere, `The barcode "${v.barcode}" can only use letters, digits and -.`);
          ok = false;
        } else if (barcodeOwner.has(v.barcode)) {
          err(vwhere, `The barcode is also used by ${barcodeOwner.get(v.barcode)}.`);
          ok = false;
        } else barcodeOwner.set(v.barcode, vwhere);
      }
      if (v.price.kind === 'invalid') {
        err(vwhere, `The price "${v.price.text}" is not an amount.`);
        ok = false;
      }
      if (v.cost === 'invalid') {
        err(vwhere, 'The cost is not an amount.');
        ok = false;
      } else if (v.cost !== null && v.cost > 0 && !settings.importCosts) costsIgnored++;
      if (multi) {
        const vname = v.optionValues.filter((x) => x !== '').join(' / ');
        if (!vname) {
          err(vwhere, 'A variant of an item with several variants has no option value.');
          ok = false;
        } else if (vname.length > CATALOG_LIMITS.variantName) {
          err(
            vwhere,
            `The variant name "${vname}" is longer than ${CATALOG_LIMITS.variantName} characters.`,
          );
          ok = false;
        } else if (variantNames.has(lower(vname))) {
          err(vwhere, `Two variants are both named "${vname}".`);
          ok = false;
        } else variantNames.add(lower(vname));
      }
    }
    for (const m of item.modifiers)
      if (
        !groupIdByName.has(lower(m)) &&
        !(sheet?.groups ?? []).some((g) => lower(g.name) === lower(m))
      )
        missingModifiers.set(m, (missingModifiers.get(m) ?? 0) + 1);
    const choice = settings.routeByCategory[lower(item.category)];
    if (!choice) {
      // reported once per category below
    } else if (choice !== 'NONE' && !routeIdByCode.has(choice)) {
      err(
        where,
        `The ${choice === 'KITCHEN' ? 'Kitchen' : 'Bar'} route does not exist or is archived.`,
      );
      ok = false;
    }
    if (item.composite)
      warn(where, 'A composite item: it is imported as one item; its components are not.');
    if (item.trackStock) stockIgnored++;
    if (ok) importable.push({ item, variants: forSale });
  }
  for (const [m, n] of missingModifiers)
    err(
      `Modifier "${m}"`,
      `${n} item(s) use it, but the modifier sheet does not list its options. Add its options to the sheet (Modifier,Option,Price,Min,Max).`,
    );
  const unrouted = new Map<string, number>();
  for (const { item } of importable)
    if (!settings.routeByCategory[lower(item.category)])
      unrouted.set(item.category, (unrouted.get(item.category) ?? 0) + 1);
  for (const [c, n] of unrouted)
    err(
      c ? `Category "${c}"` : 'Items without a category',
      `Choose where its ${n} item(s) are prepared: Kitchen, Bar or no preparation.`,
    );
  if (costsIgnored > 0)
    info(
      'Costs',
      `${costsIgnored} variant cost(s) in the file are not imported (you chose not to import costs).`,
    );
  if (stockIgnored > 0)
    info(
      'Stock',
      `${stockIgnored} item(s) track stock in Loyverse; stock levels are not imported.`,
    );

  // ── categories ──
  const categoryIdByKey = new Map<string, string>();
  for (const { item } of importable) {
    const key = lower(item.category);
    if (!key || categoryIdByKey.has(key)) continue;
    const id = importEntityId(biz, 'category', key);
    const liveCategory = snapshot.categories.find((x) => !x.archived_at && lower(x.name) === key);
    let existing =
      snapshot.categories.find((x) => x.id === id) ??
      snapshot.categories.find((x) => loyverseRef(x)?.category === key) ??
      liveCategory;
    if (existing?.archived_at && liveCategory) existing = liveCategory;
    categoryIdByKey.set(key, existing?.id ?? id);
    if (!existing) {
      step(
        categorySteps,
        'category.create',
        key,
        { id, name: item.category, external_refs: { loyverse: { category: key } } },
        'category',
        'create',
        `Category "${item.category}"`,
      );
      continue;
    }
    if (existing.archived_at)
      step(
        categorySteps,
        'category.restore',
        key,
        { category_id: existing.id },
        'category',
        'restore',
        `Restore category "${item.category}"`,
      );
    else if (existing.id !== id && !loyverseRef(existing))
      info(`Category "${item.category}"`, 'An existing category with this name is used.');
    if (existing.name !== item.category)
      step(
        categorySteps,
        'category.update',
        key,
        { category_id: existing.id, name: item.category },
        'category',
        'update',
        `Category "${existing.name}" renamed "${item.category}"`,
      );
  }

  // ── products, variants and modifier assignments ──
  const costOf = new Map(snapshot.costs.map((c) => [c.product_variant_id, c.cost_minor]));
  const importedProductIds = new Set<string>();
  for (const { item, variants } of importable) {
    counts.importedItems++;
    const before = counts.create + counts.update + counts.restore + counts.assign;
    const where = `"${item.name}"`;
    const handle = item.key;
    const productId = importEntityId(biz, 'product', handle);
    const existing =
      snapshot.products.find((x) => x.id === productId) ??
      snapshot.products.find((x) => loyverseRef(x)?.handle === handle);
    const clash = snapshot.products.find(
      (x) => !x.archived_at && lower(x.name) === lower(item.name) && x.id !== existing?.id,
    );
    if (clash) {
      err(
        where,
        'A product with this name already exists in didto. OS and did not come from this import. Rename one of them first.',
      );
      continue;
    }
    const choice = settings.routeByCategory[lower(item.category)];
    const routeId = choice && choice !== 'NONE' ? (routeIdByCode.get(choice) ?? null) : null;
    const categoryId = item.category ? (categoryIdByKey.get(lower(item.category)) ?? null) : null;
    const descriptionText = htmlToText(item.description);
    let description: string | null = descriptionText === '' ? null : descriptionText;
    if (description && description.length > CATALOG_LIMITS.description) {
      warn(where, `The description is cut to ${CATALOG_LIMITS.description} characters.`);
      description = Array.from(description).slice(0, CATALOG_LIMITS.description).join('').trimEnd();
    }
    const multi = variants.length > 1;
    const nameOf = (v: LoyverseVariant) =>
      multi ? v.optionValues.filter((x) => x !== '').join(' / ') : null;
    const priceOf = (v: LoyverseVariant) => (v.price.kind === 'fixed' ? v.price.minor : 0);
    const costFor = (v: LoyverseVariant) =>
      settings.importCosts && typeof v.cost === 'number' && v.cost > 0 ? v.cost : null;
    const productLiveId = existing?.id ?? productId;
    importedProductIds.add(productLiveId);

    // SKUs and barcodes already used elsewhere in the catalog
    const others = snapshot.variants.filter(
      (x) => !x.archived_at && x.product_id !== productLiveId,
    );
    let clashes = false;
    for (const v of variants) {
      const sku = normalizeSku(v.sku)!;
      const other = others.find((x) => x.sku === sku);
      if (other) {
        const owner = snapshot.products.find((p) => p.id === other.product_id);
        err(
          `${where}, SKU ${sku}`,
          `The SKU is already used by "${owner?.name ?? 'another product'}" in didto. OS.`,
        );
        clashes = true;
      }
      if (v.barcode && others.some((x) => x.barcode === v.barcode)) {
        err(
          `${where}, barcode ${v.barcode}`,
          'The barcode is already used by another product in didto. OS.',
        );
        clashes = true;
      }
    }
    if (clashes) continue;

    if (!existing) {
      const first = variants[0]!;
      const cost = costFor(first);
      step(
        productSteps,
        'product.create',
        handle,
        {
          id: productId,
          name: item.name,
          category_id: categoryId,
          description,
          production_route_id: routeId,
          price_minor: priceOf(first),
          ...(cost !== null ? { cost_minor: cost } : {}),
          sku: normalizeSku(first.sku),
          barcode: first.barcode,
          external_refs: { loyverse: { handle } },
        },
        'product',
        'create',
        `${item.name} (${money(priceOf(first))}${multi ? `, ${variants.length} variants` : ''})`,
      );
      variants.slice(1).forEach((v, i) => {
        const vkey = `${handle}|${normalizeSku(v.sku)}`;
        const vcost = costFor(v);
        step(
          variantSteps,
          'variant.create',
          vkey,
          {
            id: importEntityId(biz, 'variant', vkey),
            product_id: productId,
            name: nameOf(v)!,
            ...(i === 0 ? { default_name: nameOf(first)! } : {}),
            price_minor: priceOf(v),
            ...(vcost !== null ? { cost_minor: vcost } : {}),
            sku: normalizeSku(v.sku),
            barcode: v.barcode,
            external_refs: { loyverse: { handle, sku: normalizeSku(v.sku) } },
          },
          'variant',
          'create',
          `${item.name}: variant "${nameOf(v)}" (${money(priceOf(v))})`,
        );
      });
    } else {
      if (existing.archived_at)
        step(
          productSteps,
          'product.restore',
          handle,
          { product_id: existing.id },
          'product',
          'restore',
          `Restore ${item.name}`,
        );
      const ch: Payload<'product.update'> = { product_id: existing.id };
      const what: string[] = [];
      if (existing.name !== item.name) {
        ch.name = item.name;
        what.push('name');
      }
      if (existing.category_id !== categoryId) {
        ch.category_id = categoryId;
        what.push('category');
      }
      if ((existing.description ?? null) !== description) {
        ch.description = description;
        what.push('description');
      }
      if (existing.production_route_id !== routeId) {
        ch.production_route_id = routeId;
        what.push('preparation');
      }
      if (what.length > 0)
        step(
          productSteps,
          'product.update',
          handle,
          ch,
          'product',
          'update',
          `${item.name}: ${what.join(', ')}`,
        );

      const own = snapshot.variants.filter((x) => x.product_id === existing.id);
      const liveOwn = own.filter((x) => !x.archived_at);
      const liveDefault = liveOwn.find((x) => x.is_default) ?? null;
      let defaultNameAfter = liveDefault?.name ?? null;
      const createLater: LoyverseVariant[] = [];
      // a variant of the catalog answers one row of the file at most
      const taken = new Set<string>();
      for (const v of variants) {
        const sku = normalizeSku(v.sku)!;
        const hit =
          own.find((x) => x.id === importEntityId(biz, 'variant', `${handle}|${sku}`)) ??
          own.find((x) => x.sku === sku);
        if (hit) taken.add(hit.id);
      }
      variants.forEach((v, i) => {
        const sku = normalizeSku(v.sku)!;
        const vkey = `${handle}|${sku}`;
        const vid = importEntityId(biz, 'variant', vkey);
        let match = own.find((x) => x.id === vid) ?? own.find((x) => x.sku === sku);
        // several variants: a live one of the same name is this row, even if its SKU changed in
        // Loyverse (adding it again would be refused: variant names are unique per product)
        if (!match && multi) {
          const wantedName = lower(nameOf(v) ?? '');
          match = liveOwn.find(
            (x) => !taken.has(x.id) && x.name !== null && lower(x.name) === wantedName,
          );
          if (match) taken.add(match.id);
        }
        // one variant on both sides: the same variant, even if its SKU changed in Loyverse
        if (!match && !multi && liveOwn.length === 1) match = liveOwn[0];
        // the first variant of a product made by hand may still be its SKU-less default
        if (!match && i === 0 && liveDefault && liveDefault.sku === null) match = liveDefault;
        if (!match) {
          createLater.push(v);
          return;
        }
        if (match.archived_at)
          step(
            variantSteps,
            'variant.restore',
            vkey,
            { variant_id: match.id },
            'variant',
            'restore',
            `${item.name}: restore variant ${sku}`,
          );
        const vc: Payload<'variant.update'> = { variant_id: match.id };
        const vwhat: string[] = [];
        // a single-variant item leaves the variant's name alone (the product may hold more variants)
        const wanted = multi ? nameOf(v) : match.name;
        if (wanted !== match.name) {
          vc.name = wanted;
          vwhat.push('name');
          if (match.id === liveDefault?.id) defaultNameAfter = wanted;
        }
        if (match.price_minor !== priceOf(v)) {
          vc.price_minor = priceOf(v);
          vwhat.push(`price ${money(match.price_minor)} → ${money(priceOf(v))}`);
        }
        if (match.sku !== sku) {
          vc.sku = sku;
          vwhat.push('SKU');
        }
        if ((match.barcode ?? null) !== v.barcode) {
          vc.barcode = v.barcode;
          vwhat.push('barcode');
        }
        const cost = costFor(v);
        if (cost !== null && costOf.get(match.id) !== cost) {
          vc.cost_minor = cost;
          vwhat.push('cost');
        }
        if (vwhat.length > 0)
          step(
            variantSteps,
            'variant.update',
            vkey,
            vc,
            'variant',
            'update',
            `${item.name}${multi ? ` "${nameOf(v)}"` : ''}: ${vwhat.join(', ')}`,
          );
      });
      // A new variant needs a name, and its product's default must already have one (or get one
      // above). The import never invents a name: such a case is for the owner to settle by hand.
      if (createLater.length > 0 && (!multi || defaultNameAfter === null)) {
        err(
          where,
          !multi
            ? `didto. OS holds ${liveOwn.length} variants for this item and none has its SKU ${normalizeSku(createLater[0]!.sku)}: set the SKUs in didto. OS to match Loyverse first.`
            : `Its variant in didto. OS has no name and is not in the file, so the new variants cannot be added: name or update that variant in didto. OS first.`,
        );
        createLater.length = 0;
      }
      createLater.forEach((v) => {
        const sku = normalizeSku(v.sku)!;
        const vkey = `${handle}|${sku}`;
        const vcost = costFor(v);
        const name = nameOf(v)!;
        step(
          variantSteps,
          'variant.create',
          vkey,
          {
            id: importEntityId(biz, 'variant', vkey),
            product_id: existing.id,
            name,
            price_minor: priceOf(v),
            ...(vcost !== null ? { cost_minor: vcost } : {}),
            sku,
            barcode: v.barcode,
            external_refs: { loyverse: { handle, sku } },
          },
          'variant',
          'create',
          `${item.name}: variant "${name}" (${money(priceOf(v))})`,
        );
      });
    }
    for (const m of item.modifiers) {
      const gid = groupIdByName.get(lower(m));
      if (!gid) continue;
      if (
        snapshot.assignments.some(
          (a) => a.product_id === productLiveId && a.modifier_group_id === gid,
        )
      )
        continue;
      step(
        assignSteps,
        'product.assign_modifier_group',
        `${handle}|${lower(m)}`,
        { product_id: productLiveId, modifier_group_id: gid },
        'assignment',
        'assign',
        `${item.name}: modifier group "${m}"`,
      );
    }
    if (counts.create + counts.update + counts.restore + counts.assign === before)
      counts.unchangedItems++;
  }
  const leftAlone = snapshot.products.filter(
    (p) => !p.archived_at && !importedProductIds.has(p.id),
  ).length;
  if (leftAlone > 0)
    info(
      'Catalog',
      `${leftAlone} product(s) in didto. OS are not in the file; the import leaves them as they are.`,
    );

  const blocked = problems.some((p) => p.level === 'error');
  return {
    steps: [
      ...groupSteps,
      ...optionSteps,
      ...categorySteps,
      ...productSteps,
      ...variantSteps,
      ...assignSteps,
    ],
    problems,
    counts,
    blocked,
  };
}

/** The modifier sheet as a CSV template (header and one example group), for the owner to fill. */
export function modifierSheetTemplate(): string {
  return [
    MODIFIER_SHEET_HEADER.join(','),
    'Milk,Oat milk,30.00,0,1',
    'Milk,Soy milk,25.00,,',
    'Extra shot,Extra shot,40.00,0,2',
  ].join('\r\n');
}

/** Keep TypeScript honest: every planned kind's payload is that command's payload. */
export type ImportPayload = PlannedStep['payload'];
export type { ExternalRefs };
